import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import printJS from 'print-js';
import PedidoReprintView from './../modal-reporte/pedido-reprint';

let __lastPrintSignature = '';
let __lastPrintAt = 0;

const BASE_PRINT_STYLES = `
  @page { size: A4; margin: 10mm; }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  #printReprintContainer {
    display: block !important;
    width: 100% !important;
  }

  #printReprintContainer,
  #printReprintContainer * {
    box-sizing: border-box !important;
    overflow: visible !important;
    filter: none !important;
  }

  #printReprintContainer .almacen-header {
    display: table !important;
    width: 100% !important;
  }

  #printReprintContainer .almacen-header > * {
    display: table-cell !important;
    vertical-align: top !important;
  }

  table {
    width: 100% !important;
    border-collapse: collapse !important;
    margin-left: 0 !important;
    table-layout: fixed !important;
  }

  th, td {
    vertical-align: top !important;
    word-break: break-word !important;
  }

  thead { display: table-header-group; }
  tfoot { display: table-footer-group; }
  tr { break-inside: auto !important; page-break-inside: auto !important; }
  td, th { break-inside: auto !important; page-break-inside: auto !important; }

  .print-order {
    break-after: page !important;
    page-break-after: always !important;
  }

  .print-order:last-child {
    break-after: auto !important;
    page-break-after: auto !important;
  }

  .rectangulo-almacen {
    width: 60px !important;
    height: 40px !important;
    border: 2px solid rgb(5, 239, 21) !important;
    margin: 0 auto !important;
  }
`;

const COPY_WATERMARK_STYLES = `
  body::before {
    content: "COPIA" !important;
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) rotate(-32deg) !important;
    transform-origin: center center !important;

    z-index: 999999 !important;
    display: block !important;

    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 180px !important;
    font-weight: 900 !important;
    letter-spacing: 2px !important;
    line-height: 1 !important;
    text-transform: uppercase !important;
    white-space: nowrap !important;
    text-align: center !important;

    color: rgba(90, 90, 90, 0.18) !important;
    text-shadow: none !important;

    pointer-events: none !important;
    user-select: none !important;
  }
`;

function getPrintStyles(showCopy) {
  return `${BASE_PRINT_STYLES}\n${showCopy ? COPY_WATERMARK_STYLES : ''}`;
}

function shouldShowCopyWatermark(item) {
  if (!item) return false;
  if (item.show_copy_watermark === true) return true;
  if (item.is_copy === true) return true;
  return false;
}
async function waitForPrintReady() {
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch (_) {}

  const container = document.getElementById('printReprintContainer');
  if (!container) return;

  const imgs = Array.from(container.querySelectorAll('img'));
  await Promise.all(
    imgs.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((res) => {
        img.onload = res;
        img.onerror = res;
      });
    })
  );

  await new Promise((r) => requestAnimationFrame(r));
}

export default function ModalPedidoPrint({ open, data, onClose }) {
  const items = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data.filter(Boolean) : [data];
  }, [data]);

  const showCopyWatermark = useMemo(() => {
    return items.some(shouldShowCopyWatermark);
  }, [items]);

  const closeOnceRef = useRef(false);
  const timersRef = useRef({ t1: null, t2: null });

  useEffect(() => {
    if (!open) closeOnceRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!open || items.length === 0) return;
  
    const timers = timersRef.current; 
    let cancelled = false;
  
    const signature = items
      .map((it) =>
        [
          String(it?.folio || it?.erp_order_id || it?.order_id || ''),
          String(
            it?.print_count ??
            it?.printCount ??
            it?.show_copy_watermark ??
            it?.is_copy ??
            ''
          ),
        ].join(':')
      )
      .join('|');
  
    const now = Date.now();
    if (signature && __lastPrintSignature === signature && now - __lastPrintAt < 900) {
      return;
    }
  
    __lastPrintSignature = signature;
    __lastPrintAt = now;
  
    const safeClose = () => {
      if (closeOnceRef.current) return;
      closeOnceRef.current = true;
      if (typeof onClose === 'function') onClose();
    };
  
    if (timers?.t1) clearTimeout(timers.t1);
    if (timers?.t2) clearTimeout(timers.t2);
  
    timers.t1 = setTimeout(async () => {
      try {
        await waitForPrintReady();
        if (cancelled) return;
  
        printJS({
          printable: 'printReprintContainer',
          type: 'html',
          style: getPrintStyles(showCopyWatermark),
          targetStyles: [
            'font',
            'font-size',
            'font-weight',
            'font-family',
            'color',
            'text-align',
            'border',
            'border-collapse',
            'padding',
            'margin',
            'width',
          ],
          onPrintDialogClose: safeClose,
        });
  
        timers.t2 = setTimeout(safeClose, 500);
      } catch (e) {
        safeClose();
      }
    }, 0);
  
    return () => {
      cancelled = true;
  
      if (timers?.t1) clearTimeout(timers.t1);
      if (timers?.t2) clearTimeout(timers.t2);
  
      if (timers) {
        timers.t1 = null;
        timers.t2 = null;
      }
    };
  }, [open, items, onClose, showCopyWatermark]);

  if (!open || items.length === 0) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.35)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: 12,
          borderRadius: 8,
          maxWidth: 960,
          width: '95%',
          maxHeight: '92vh',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <h4 style={{ margin: 0 }}>
            Vista previa de impresión {items.length > 1 ? `(${items.length} pedidos)` : ''}
          </h4>

          <button
            onClick={onClose}
            type="button"
            style={{
              border: 'none',
              background: '#f44336',
              color: '#fff',
              padding: '6px 10px',
              borderRadius: 4,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Cerrar
          </button>
        </div>

        <div id="printReprintContainer" style={{ marginTop: 10 }}>
{items.map((it, idx) => {
  const safePrintProps = {
    ...it,
    watermark_text: "",
    watermarkText: "",
  };

  return (
    <section
      key={it?.folio || it?.erp_order_id || it?.order_id || idx}
      className="print-order"
    >
      <PedidoReprintView onProps={safePrintProps} />
    </section>
  );
})}        </div>
      </div>
    </div>,
    document.body
  );
}